/**
 * Workflow GET returns a transcript prefix. The paginated endpoint is 0-indexed
 * by server snippet count, so the next fetch must start at prefix.length — not 0
 * (overlaps) and not a hardcoded 25 (drifts if the prefix size changes).
 */

export const TRANSCRIPT_PAGE_SIZE = 5000;
export const MAX_TRANSCRIPT_PAGES = 40;
export const TRANSCRIPT_PAGE_CAP_MESSAGE =
  "This transcript is too large to load completely. Some later paragraphs may be missing.";

export function concatTranscriptPages(prefix = [], page = []) {
  return (prefix || []).concat(page || []);
}

export function nextTranscriptPageStart(prefix = []) {
  return Array.isArray(prefix) ? prefix.length : 0;
}

export function serverTranscriptCount(transcripts = []) {
  if (!Array.isArray(transcripts)) return 0;
  return transcripts.filter((t) => !t?.isRedHerring).length;
}

export function canFetchNewTranscripts(editor) {
  return editor?.transcriptsInitialized === true;
}

export function shouldApplyInFlightTranscript(editor, workflowId) {
  if (workflowId == null || workflowId === "") return false;
  if (!canFetchNewTranscripts(editor)) return false;

  const currentId = editor?.workflowData?.id;
  if (currentId == null || currentId === "") return false;

  return String(currentId) === String(workflowId);
}

export function appendLiveStorePage(currentStore, incomingPage) {
  const store = Array.isArray(currentStore) ? currentStore : [];
  const page = Array.isArray(incomingPage) ? incomingPage : [];
  const existingIds = new Set(store.map((t) => t?.id).filter((id) => id != null));
  const uniquePage = page.filter((t) => t?.id == null || !existingIds.has(t.id));

  return {
    nextStore: store.concat(uniquePage),
    storeLengthBeforeAppend: store.length,
  };
}

export async function fetchRemainingTranscriptPages(
  id,
  start,
  fetchPage,
  pageSize = TRANSCRIPT_PAGE_SIZE
) {
  const items = [];
  let offset = Math.max(0, Number(start) || 0);
  let pageCount = 0;
  let lastWindowKey = null;

  while (pageCount < MAX_TRANSCRIPT_PAGES) {
    const response = await fetchPage(id, offset, pageSize);
    const page = response?.transcript || [];

    if (!page.length) {
      return { items, hitPageCap: false };
    }

    const windowKey = `${page[0]?.id ?? ""}:${page[page.length - 1]?.id ?? ""}:${page.length}`;
    if (lastWindowKey !== null && windowKey === lastWindowKey) {
      return { items, hitPageCap: false };
    }

    items.push(...page);

    if (page.length < pageSize) {
      return { items, hitPageCap: false };
    }

    offset += page.length;
    lastWindowKey = windowKey;
    pageCount += 1;
  }

  return { items, hitPageCap: true };
}
